/**
 * **组节点资产** —— 一张搭好的子图，当**单个节点**用。
 *
 * ## 它和「组」是什么关系
 *
 * ```
 * 用户选中几个节点 → 打组                     （已有能力，useGrouping）
 *        ↓  得到一个 type: 'group' 的节点，内部是那张图
 * 把那个组的**内部图**存下来            → 资产   （本文件）
 *        ↓
 * 从菜单里放一个，就是一个现成的节点     （像「日期计算」那样，打开就能用）
 * ```
 *
 * ⚠️ 所以资产不是新机制，而是**既有组机制的一份可复用快照**。
 * 求值走 `groupEvaluator`、进组编辑走 `useSubgraphLevels` —— 一行都不用改。
 *
 * ## 接口**推导**出来，不另存一份
 *
 * 资产只存 `graph`。对外的端口（行）由内部图**算**出来：
 *   · 输入路数 = 内部 `group.input` 节点的输出行数（`groupInputRows` 造的）；
 *   · 输出路数 = 内部 `group.output` 节点变参行上**实际接了几路**。
 *
 * ⚠️ 存两份（图 + 行）就会漂移：有人改了里面的接口节点，外面的端口还是旧的，
 * 表现是「资产端口对不上内部」这种极难查的问题。
 *
 * ## ⚠️ 落进文档就**固化**
 *
 * 资产被放到画布上时**拷贝一份**（`makeAssetModel` 深拷贝 graph）。
 * 之后改资产**不会**影响已经用它的文档 —— 宁可「修了资产要逐个文档更新」，
 * 也不要「打开老文档发现行为变了」（设计文档 §2 的取舍）。
 */
import {
  GROUP_INPUT_NODE,
  GROUP_IN_REPEATER,
  GROUP_OUTPUT_NODE,
  groupRowsFor,
  groupRowsFromGraph,
  innerInterfaceCount,
} from './group'
import type { GraphNodeModel, NodeRow, SubGraph } from '../types/node'

export interface GroupAssetDef {
  /** 稳定标识，形如 `asset.format-date` */
  id: string
  /** 菜单里显示的名字 */
  label: string
  /** 菜单分组（哪个包 / 哪一类） */
  category: string
  keywords: string[]
  /** 一句话说明「这个资产做什么」 */
  hint?: string
  /**
   * 内部那张图 —— **接口由它推导**。
   *
   * 必须包含 `group.input` / `group.output` 两个接口节点，否则放出来
   * 就是一个没有端口、也算不出东西的空壳（`assetProblems` 会报出来）。
   */
  graph: SubGraph
  /**
   * 端口名（可选，按顺序对应）。
   *
   * ⚠️ 没有它，端口名会是「参数 1 / 结果 1」那种 —— 一个叫
   * 「格式化日期」的资产长着三个「结果」端口，等于没写文档。
   */
  inputs?: string[]
  outputs?: string[]
}

/** 资产的问题清单（**不抛异常**：导入方要显示给人看） */
export function assetProblems(asset: GroupAssetDef): string[] {
  const problems: string[] = []
  const where = `资产「${asset.label || asset.id}」`

  if (!asset.id) problems.push(`${where}：缺少 id`)
  if (!asset.label) problems.push(`${where}：缺少 label`)

  const graph = asset.graph
  if (!graph || !Array.isArray(graph.nodes)) {
    problems.push(`${where}：缺少 graph.nodes`)
    return problems
  }
  const hasIn = graph.nodes.some((n) => n.id === GROUP_INPUT_NODE)
  const hasOut = graph.nodes.some((n) => n.id === GROUP_OUTPUT_NODE)
  if (!hasIn) problems.push(`${where}：内部图缺少接口节点 ${GROUP_INPUT_NODE}（那它就没有输入端口）`)
  if (!hasOut) problems.push(`${where}：内部图缺少接口节点 ${GROUP_OUTPUT_NODE}（那它就没有输出端口）`)
  if (!Array.isArray(graph.edges)) problems.push(`${where}：graph.edges 必须是数组`)

  /* 端口名给了就必须给够 —— 少一个会静默沿用旧名字 */
  const inputs = countAssetInputs(graph)
  const outputs = countAssetOutputs(graph)
  if (asset.inputs && asset.inputs.length !== inputs) {
    problems.push(`${where}：inputs 给了 ${asset.inputs.length} 个，但内部图有 ${inputs} 个输入`)
  }
  if (asset.outputs && asset.outputs.length !== outputs) {
    problems.push(`${where}：outputs 给了 ${asset.outputs.length} 个，但内部图有 ${outputs} 个输出`)
  }
  return problems
}


/**
 * 内部 `group.input` 节点的输出行数 = 对外的输入路数。
 *
 * ⚠️ 实现**委托**给 `group.ts` 的 `innerInterfaceCount` —— 两个地方各写一遍
 * 「数内部接口行」的后果是它们慢慢会不一致，而这是资产接口的**唯一来源**。
 */
export function countAssetInputs(graph: SubGraph): number {
  return innerInterfaceCount(graph)
}

/** 内部 `group.output` 变参行上**实际接了几路** = 对外的输出路数 */
export function countAssetOutputs(graph: SubGraph): number {
  const rows = groupRowsFromGraph(graph, countAssetInputs(graph))
  return rows.filter((r) => r.kind === 'output').length
}

/**
 * 由内部图推导出**外层**的行（端口）。
 *
 * 这就是「不存两份」的落点：`.np-node` 上的端口完全来自这里。
 */
export function assetRows(asset: GroupAssetDef): NodeRow[] {
  const rows = groupRowsFromGraph(asset.graph, countAssetInputs(asset.graph))

  /*
   * 端口名（可选）—— 输入、输出两边都是**逐个静态行改 `label`**。
   *
   * ⚠️ 输入侧以前是变参行，名字写在 `names`（各实例的名字）上；
   * 现在外层输入是静态行，名字就在行自己的 `label` 上。
   * 两边形状一样了，代码也就不用分两种情况。
   */
  const labels: [NodeRow[], readonly string[] | undefined][] = [
    [rows.filter((r) => r.kind === 'input'), asset.inputs],
    [rows.filter((r) => r.kind === 'output'), asset.outputs],
  ]
  for (const [group, names] of labels) {
    if (!names) continue
    for (const [index, label] of names.entries()) {
      const row = group[index]
      if (row && label) row.label = label
    }
  }
  return rows
}

/**
 * 把资产变成一个**可以放进画布的节点模型**。
 *
 * ⚠️ 类型固定是 `'group'` —— 求值器、进组编辑、子图层级全都照旧走组那一套。
 * 资产的身份体现在 `title` 上（画布上看得见「格式化日期」），
 * 而**不**是一个新的 `type`：那样每个资产都要注册一种节点类型，
 * 而它们的行为其实完全一样。
 *
 * ⚠️ `graph` 深拷贝：资产是共享的（多个文档/多次放置），
 * 而节点模型是可改的（进组编辑会改它）。
 */
export function makeAssetModel(asset: GroupAssetDef, id: string): GraphNodeModel {
  const rows = assetRows(asset)
  return {
    id,
    type: 'group',
    title: asset.label,
    category: 'utility',
    width: 220,
    rows: rows.map((row) => ({ ...row })) as NodeRow[],
    graph: JSON.parse(JSON.stringify(asset.graph)) as SubGraph,
  }
}

/** 一个资产长什么样（菜单里那行小字用） */
export function describeAsset(asset: GroupAssetDef): string {
  const inputs = countAssetInputs(asset.graph)
  const outputs = countAssetOutputs(asset.graph)
  return `${inputs} 入 / ${outputs} 出`
}

/** 断言式校验：给测试和「存为资产」用 —— 有端口才算一张能用的资产 */
export function isUsableAsset(asset: GroupAssetDef): boolean {
  return assetProblems(asset).length === 0
}

/** 资产的行里有没有那个接口变参行（测试用） */
export function hasInputRepeater(rows: readonly NodeRow[]): boolean {
  return rows.some((r) => r.kind === 'repeater' && r.id === GROUP_IN_REPEATER)
}

export { groupRowsFor }
